import { AxDataTableDynamicRequestParams, AxDataTableRequestInput, AxDataTableResponse } from "@/lib/types";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import { useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { BaseDataTable } from "@/components/dataTable/BaseDataTable";
import { Pagination } from "../common/Pagination";
import { AxDataTableHeader } from "../common/AxDataTableHeader";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { useContainerWidth } from "@/hooks/useContainerWidth";
import { LoadingOverlay } from "@/components/LoadingOverlay";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { DownloadIcon } from "lucide-react";

interface AxDataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  uniqQueryKey: string;
  fetcher: (
    input: AxDataTableRequestInput,
    dynamicRequestParams?: AxDataTableDynamicRequestParams
  ) => Promise<AxiosResponse<AxDataTableResponse<TData>>>;
  clientSideFilter?: (data: TData[]) => TData[];
  clientSideSort?: (data: TData[]) => TData[];
  dynamicRequestParams?: AxDataTableDynamicRequestParams;
  extraHeader?: React.ReactNode;
  getRowId: (row: TData) => string;
  onReorder?: (newData: TData[]) => void;
  numberOfObjectsPerPage?: number;
  hidePageSizeSelector?: boolean;
  onDeleteRows?: (ids: any[]) => Promise<void>;
  deleting?: boolean;
  onClickSort?: (sorting: SortingState) => void;
  sortableColumns?: (keyof TData & string)[];
  fileName?: string;
  exportableColumns?: (keyof TData & string)[];
  noResultsText?: string;
}

const DEFAULT_PAGE_SIZE = 10;

function convertToCSV<TData>(columns: string[], data: TData[]): string {
  const headers = columns.map((column) => {
    const headerText = column || "";
    return `"${headerText.replace(/"/g, '""')}"`;
  });

  // Create data rows
  const rows = data.map((item) => {
    return columns
      .map((column) => {
        const stringValue = (item as any)[column as any] || "";
        return `"${stringValue.replace(/"/g, '""')}"`;
      })
      .join(",");
  });

  // Combine headers and rows
  return [headers.join(",")].concat(rows).join("\n");
}

// Helper function to download CSV
function downloadCSV(csvContent: string, fileName: string): void {
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.setAttribute("href", url);
  link.setAttribute("download", fileName);
  link.style.visibility = "hidden";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export function AxDataTable<TData, TValue>({
  columns,
  uniqQueryKey,
  fetcher,
  clientSideFilter,
  clientSideSort,
  dynamicRequestParams,
  extraHeader,
  getRowId,
  onDeleteRows,
  deleting,
  onReorder,
  numberOfObjectsPerPage = DEFAULT_PAGE_SIZE,
  hidePageSizeSelector = false,
  onClickSort,
  sortableColumns,
  fileName = "export.csv",
  exportableColumns,
}: AxDataTableProps<TData, TValue>) {
  const { t } = useTranslation();
  const [input, setInput] = useState<AxDataTableRequestInput>({
    numberOfObjectsPerPage,
    pageNumber: 1,
    search: "",
  });

  const { data, isLoading, isRefetching, refetch, error } = useQuery({
    queryKey: [uniqQueryKey, input, dynamicRequestParams],
    queryFn: () => fetcher(input, dynamicRequestParams),
    placeholderData: keepPreviousData,
    refetchOnMount: true,
    refetchInterval: 30 * 1000,
  });

  const dataResult = clientSideFilter
    ? clientSideFilter(data?.data.result || [])
    : ((data?.data.result || []) as TData[]);

  const dataResultSorted = clientSideSort ? clientSideSort(dataResult) : dataResult;

  const pageSizeOptions = data?.data?.dropdownList || [];
  const isUiLoading = isRefetching || isLoading;

  const refreshData = () => {
    refetch();
  };

  const handleExportCSV = () => {
    const csvContent = convertToCSV(exportableColumns as string[], dataResultSorted);
    downloadCSV(csvContent, fileName);
  };

  const exportButton = (
    <Button
      onClick={handleExportCSV}
      size="sm"
      variant="outline"
      className="h-[30px] ml-2"
      disabled={isUiLoading || dataResultSorted.length === 0}
    >
      <DownloadIcon className="mr-1 h-4 w-4" />
      {t("common.exportCSV", "CSV")}
    </Button>
  );

  const { containerRef, width } = useContainerWidth(isUiLoading);

  const combinedExtraHeader = (
    <>
      {extraHeader}
      {exportableColumns?.length && exportButton}
    </>
  );

  return (
    <div ref={containerRef} className="w-full">
      <AxDataTableHeader
        filterValue={input.search || ""}
        totalRecord={data?.data.totalRecord || 0}
        pageSizeOptions={pageSizeOptions}
        defaultPageSize={DEFAULT_PAGE_SIZE}
        hidePageSizeSelector={hidePageSizeSelector}
        onFilterChange={(value) => {
          setInput((prevInput) => ({
            ...prevInput,
            search: value,
            pageNumber: 1,
          }));
        }}
        onPageSizeChange={(value) => {
          setInput((prevInput) => ({
            ...prevInput,
            numberOfObjectsPerPage: value,
          }));
        }}
        onRefreshClick={refreshData}
        isLoading={isUiLoading}
        extraHeader={combinedExtraHeader}
      />
      {error ? (
        <Alert className="mt-2" variant="destructive">
          <AlertTitle>{t("common.error")}</AlertTitle>
          <AlertDescription>{error?.message || t("dataTable.error")}</AlertDescription>
        </Alert>
      ) : null}
      <ScrollArea style={{ width: width }} className="mt-2">
        <LoadingOverlay isLoading={isUiLoading}>
          <BaseDataTable
            columns={columns}
            data={dataResultSorted}
            rowCount={input.numberOfObjectsPerPage}
            onReorder={onReorder}
            getRowId={getRowId}
            onDeleteRows={onDeleteRows}
            deleting={deleting}
            onClickSort={onClickSort}
            sortableColumns={sortableColumns}
          />
        </LoadingOverlay>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
      <Pagination
        currentPageNumber={input.pageNumber}
        numberOfObjectsPerPage={input.numberOfObjectsPerPage}
        totalRecord={data?.data.totalRecord || 0}
        onPageChange={(newPageNumber) => {
          setInput((prevInput) => ({
            ...prevInput,
            pageNumber: newPageNumber,
          }));
        }}
        isLoading={isUiLoading}
      />
    </div>
  );
}
